import { z } from 'zod';
import { DeviceCapabilitiesSchema } from './device-telemetry.js';

/**
 * User profile + preferences. Cognito is the credential store (email,
 * password, MFA); this is the application-side profile mirror persisted in
 * DynamoDB (table `dgm-{env}-users`). `sub` is the Cognito subject claim and
 * the DDB partition key.
 */

export const UserRole = z.enum(['owner', 'admin', 'member', 'viewer']);
export type UserRole = z.infer<typeof UserRole>;

export const ThemePreference = z.enum(['light', 'dark', 'system']);
export type ThemePreference = z.infer<typeof ThemePreference>;

export const UserPreferencesSchema = z.object({
  theme: ThemePreference.default('system'),
  locale: z.string().default('en-US'),
  density: z.enum(['comfortable', 'compact']).default('comfortable'),
  /** Non-essential processing opt-outs (GDPR/CCPA). Default to opted-out. */
  analyticsOptIn: z.boolean().default(false),
  marketingEmailsOptIn: z.boolean().default(false),
});
export type UserPreferences = z.infer<typeof UserPreferencesSchema>;

export const UserProfileSchema = z.object({
  /** Cognito subject (sub claim). PK in DDB. */
  sub: z.string().min(1),
  email: z.string().email(),
  emailVerified: z.boolean().default(false),
  displayName: z.string().max(80).optional(),
  avatarKey: z.string().optional(), // S3 object key for avatar
  bio: z.string().max(500).optional(),
  role: UserRole.default('member'),
  /** Whether the user is in the OperatorAdmins Cognito group. */
  isOperatorAdmin: z.boolean().default(false),
  preferences: UserPreferencesSchema.default({}),
  /** Latest device-capability capture (CA-001; FEATURES.deviceTelemetry) —
   *  written only with preferences.analyticsOptIn consent (CA-003). Living on
   *  this item keeps it inside the GDPR export/delete path automatically. */
  deviceCapabilities: DeviceCapabilitiesSchema.optional(),
  mfaEnabled: z.boolean().default(false),
  status: z.enum(['active', 'suspended', 'pending_deletion']).default('active'),
  /** Set when the user completes first-run onboarding (A6). */
  onboardedAt: z.string().datetime().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type UserProfile = z.infer<typeof UserProfileSchema>;

/** Mutable subset a user may PATCH on themselves. */
export const UpdateProfileInput = UserProfileSchema.pick({
  displayName: true,
  bio: true,
}).partial();
export type UpdateProfileInput = z.infer<typeof UpdateProfileInput>;

export const UpdatePreferencesInput = UserPreferencesSchema.partial();
export type UpdatePreferencesInput = z.infer<typeof UpdatePreferencesInput>;
